import { API_CACHE_STORE } from './apiCache'
import { publishBrowserLocalCacheCrossTabEvent, type BrowserLocalCacheCrossTabEvent } from './crossTab'
import {
  decryptLocalCacheJson,
  deriveBrowserLocalCacheCryptoKey,
  encryptLocalCacheJson,
  isWebCryptoAvailable,
} from './crypto'
import { createIndexedDbLocalCacheDriver } from './db'
import { buildLocalCacheKey, createBrowserLocalCacheNamespace } from './keys'
import { buildLocalCacheRecord } from './storage'
import type {
  BrowserLocalCacheDriver,
  BrowserLocalCacheKeySeed,
  BrowserLocalCacheNamespace,
  BrowserLocalCacheRecord,
  BrowserLocalCacheSensitivity,
} from './types'

export type AppShellLocalCacheResource =
  | 'capabilities:preferences'
  | 'capabilities:profile'
  | 'capabilities:options'
  | 'public:app-config'
  | 'stores:bind-status'

export type AppShellLocalCacheContext = {
  driver: BrowserLocalCacheDriver
  namespace: BrowserLocalCacheNamespace
  keySeed: BrowserLocalCacheKeySeed
}

export type AppShellLocalCacheReadResult<TPayload> =
  | {
      status: 'hit'
      payload: TPayload
      record: BrowserLocalCacheRecord<never>
    }
  | {
      status: 'miss' | 'expired' | 'decrypt_failed' | 'unavailable'
      payload: null
      record?: BrowserLocalCacheRecord<never>
    }

export const APP_SHELL_CACHE_KEY_ID = 'app-shell-cache-auth-v1'
export const APP_SHELL_CAPABILITY_PREFERENCES_TTL_MS = 30 * 1000
export const APP_SHELL_CAPABILITY_PROFILE_TTL_MS = 60 * 1000
export const APP_SHELL_CAPABILITY_OPTIONS_TTL_MS = 60 * 60 * 1000
export const APP_SHELL_PUBLIC_APP_CONFIG_TTL_MS = 5 * 60 * 1000
export const APP_SHELL_STORE_BIND_STATUS_TTL_MS = 30 * 1000

const runtimeAppShellCacheDriver = createIndexedDbLocalCacheDriver()
const APP_SHELL_LOCAL_CACHE_RESOURCES: readonly AppShellLocalCacheResource[] = [
  'capabilities:preferences',
  'capabilities:profile',
  'capabilities:options',
  'public:app-config',
  'stores:bind-status',
]

export const createAppShellLocalCacheContext = (input: {
  apiBaseUrl: string
  environment?: string
  userId?: string | number | null
  authScope?: string | null
  cacheSeed: string
  driver?: BrowserLocalCacheDriver
}): AppShellLocalCacheContext | null => {
  const cacheSeed = String(input.cacheSeed || '').trim()
  if (!cacheSeed) {
    return null
  }
  return {
    driver: input.driver ?? runtimeAppShellCacheDriver,
    namespace: createBrowserLocalCacheNamespace({
      apiBaseUrl: input.apiBaseUrl,
      environment: input.environment,
      userId: input.userId,
      authScope: input.authScope,
    }),
    keySeed: {
      keyId: APP_SHELL_CACHE_KEY_ID,
      seed: cacheSeed,
      version: 1,
    },
  }
}

export const buildAppShellLocalCacheKey = (
  namespace: BrowserLocalCacheNamespace,
  resourceName: AppShellLocalCacheResource
): string => buildLocalCacheKey(namespace, resourceName, ['app-shell-cache:v1'])

export const resolveAppShellLocalCacheCrossTabResourceName = (
  event: BrowserLocalCacheCrossTabEvent
): AppShellLocalCacheResource | null => {
  const resourceName = String(event.resourceName || '').trim()
  if (APP_SHELL_LOCAL_CACHE_RESOURCES.includes(resourceName as AppShellLocalCacheResource)) {
    return resourceName as AppShellLocalCacheResource
  }
  const cacheKey = String(event.cacheKey || '')
  return APP_SHELL_LOCAL_CACHE_RESOURCES.find((resource) => cacheKey.includes(resource)) ?? null
}

export const matchesAppShellLocalCacheCrossTabEvent = (
  event: BrowserLocalCacheCrossTabEvent,
  resources: readonly AppShellLocalCacheResource[] = APP_SHELL_LOCAL_CACHE_RESOURCES
): boolean => {
  if ((event.type !== 'cache:invalidate' && event.type !== 'cache:write') || event.storeName !== API_CACHE_STORE) {
    return false
  }
  const resourceName = resolveAppShellLocalCacheCrossTabResourceName(event)
  return Boolean(resourceName && resources.includes(resourceName))
}

const resolveAppShellCryptoKey = async (context: AppShellLocalCacheContext): Promise<CryptoKey> =>
  deriveBrowserLocalCacheCryptoKey({
    seed: context.keySeed.seed,
    userScope: context.namespace.userScope,
    keyId: context.keySeed.keyId,
    encryptionVersion: context.keySeed.version,
  })

export const readAppShellLocalCache = async <TPayload>(
  context: AppShellLocalCacheContext,
  resourceName: AppShellLocalCacheResource,
  now = Date.now()
): Promise<AppShellLocalCacheReadResult<TPayload>> => {
  const cacheKey = buildAppShellLocalCacheKey(context.namespace, resourceName)
  const record = await context.driver.get<never>(API_CACHE_STORE, cacheKey)
  if (!record || record.resourceName !== resourceName) {
    return { status: 'miss', payload: null }
  }
  if (record.userScope !== context.namespace.userScope) {
    return { status: 'miss', payload: null }
  }
  if (record.expiresAt <= now || record.tombstone) {
    await context.driver.delete(API_CACHE_STORE, cacheKey)
    return { status: 'expired', payload: null, record }
  }
  if (!isWebCryptoAvailable()) {
    return { status: 'unavailable', payload: null, record }
  }
  if (!record.cipherText || !record.iv || !record.aad || record.encryption !== 'encrypted') {
    await context.driver.delete(API_CACHE_STORE, cacheKey)
    return { status: 'decrypt_failed', payload: null, record }
  }

  try {
    const key = await resolveAppShellCryptoKey(context)
    const payload = await decryptLocalCacheJson<TPayload>({
      algorithm: 'AES-GCM',
      cipherText: record.cipherText,
      encryptionVersion: record.encryptionVersion || context.keySeed.version,
      iv: record.iv,
      aad: record.aad,
      keyId: record.keyId || context.keySeed.keyId,
    }, key)
    const nextRecord: BrowserLocalCacheRecord<never> = {
      ...record,
      lastAccessedAt: now,
    }
    await context.driver.put(API_CACHE_STORE, cacheKey, nextRecord)
    return { status: 'hit', payload, record: nextRecord }
  } catch {
    await context.driver.delete(API_CACHE_STORE, cacheKey)
    return { status: 'decrypt_failed', payload: null, record }
  }
}

export const writeAppShellLocalCache = async <TPayload>(
  context: AppShellLocalCacheContext,
  resourceName: AppShellLocalCacheResource,
  payload: TPayload,
  options: {
    now?: number
    ttlMs: number
    entityType: string
    sensitivity?: BrowserLocalCacheSensitivity
    etag?: string
    lastModified?: string
    serverRevision?: string | number
    projectionVersion?: string | number
  }
): Promise<BrowserLocalCacheRecord<never> | null> => {
  if (!isWebCryptoAvailable()) {
    return null
  }
  const cacheKey = buildAppShellLocalCacheKey(context.namespace, resourceName)
  const record = buildLocalCacheRecord(context.namespace, cacheKey, resourceName, payload, {
    ttlMs: options.ttlMs,
    now: options.now,
    entityType: options.entityType,
    sensitivity: options.sensitivity ?? 'medium',
    encryption: 'encrypted',
    etag: options.etag,
    lastModified: options.lastModified,
    serverRevision: options.serverRevision,
    projectionVersion: options.projectionVersion,
    tags: ['app-shell', resourceName],
  })
  const key = await resolveAppShellCryptoKey(context)
  const envelope = await encryptLocalCacheJson(record.payload, key, {
    cacheKey,
    entityType: options.entityType,
    encryptionVersion: context.keySeed.version,
    keyId: context.keySeed.keyId,
    schemaVersion: context.namespace.schemaVersion,
    userScope: context.namespace.userScope,
  })
  const encryptedRecord: BrowserLocalCacheRecord<never> = {
    ...record,
    payload: undefined,
    cipherText: envelope.cipherText,
    iv: envelope.iv,
    aad: envelope.aad,
    encryptionVersion: envelope.encryptionVersion,
    keyId: envelope.keyId,
  }
  await context.driver.put(API_CACHE_STORE, cacheKey, encryptedRecord)
  publishBrowserLocalCacheCrossTabEvent({
    type: 'cache:write',
    storeName: API_CACHE_STORE,
    cacheKey,
    userScope: context.namespace.userScope,
    resourceName,
    reason: 'app_shell_write',
  })
  return encryptedRecord
}

export const refreshAppShellLocalCacheMetadata = async (
  context: AppShellLocalCacheContext,
  resourceName: AppShellLocalCacheResource,
  options: {
    now?: number
    ttlMs: number
    etag?: string
    lastModified?: string
    serverRevision?: string | number
    projectionVersion?: string | number
  }
): Promise<BrowserLocalCacheRecord<never> | null> => {
  const cacheKey = buildAppShellLocalCacheKey(context.namespace, resourceName)
  const record = await context.driver.get<never>(API_CACHE_STORE, cacheKey)
  if (!record || record.resourceName !== resourceName || record.userScope !== context.namespace.userScope) {
    return null
  }
  if (record.tombstone) {
    return null
  }
  const now = options.now ?? Date.now()
  const nextRecord: BrowserLocalCacheRecord<never> = {
    ...record,
    etag: options.etag ?? record.etag,
    lastModified: options.lastModified ?? record.lastModified,
    serverRevision: options.serverRevision ?? record.serverRevision,
    projectionVersion: options.projectionVersion ?? record.projectionVersion,
    updatedAt: now,
    expiresAt: now + Math.max(0, options.ttlMs),
    lastAccessedAt: now,
  }
  await context.driver.put(API_CACHE_STORE, cacheKey, nextRecord)
  return nextRecord
}

export const deleteAppShellLocalCache = async (
  context: AppShellLocalCacheContext,
  resourceName: AppShellLocalCacheResource
): Promise<void> => {
  await context.driver.delete(API_CACHE_STORE, buildAppShellLocalCacheKey(context.namespace, resourceName))
}
